// ============================================================================
// src/app/navigation/navigationDriver.ts — the router-neutral driver contract.
// ============================================================================
// WHY THIS EXISTS
// The facade hooks (useAppNavigate/useAppLocation/useAppHistory/AppLink) must
// not know WHICH router owns browser history. A navigation driver is the
// narrow contract between them and the router of record:
//
//   {
//     name: non-empty string,                // diagnostic identity
//     useNavigateFn: () => navigate,         // the router's navigate-returning hook
//     useLocationValue: () => location,      // the router's location-returning hook
//     LinkComponent: component,              // the router's Link
//   }
//
// The react-router implementation is ONE adapter (reactRouterDriver.js) and
// is the lazy default: with zero registration, the facade behaves exactly as
// the current tree does and src/main.tsx needs no change. A driver is swapped
// by calling registerNavigationDriver(driver) once, before the tree mounts —
// driver identity must stay stable for a mounted tree's lifetime because the
// facade calls the driver's hooks unconditionally (rules of hooks).
//
// LATER ROOT-JOIN MANIFEST (B2/B3, integration-owned):
//   1. the root (main.tsx composition) calls registerNavigationDriver(<the
//      TanStack adapter>) exactly once before render;
//   2. the TanStack adapter module lives in this directory and maps
//      useNavigate/useLocation/Link equivalents onto the same contract;
//   3. the lazy react-router default and the react-router adapter are deleted
//      with the router swap (Train C), and resolveNavigationDriver then
//      requires an explicit registration;
//   4. the facade's absence guards (tests/static/navigation_facade_boundary
//      _static.test.js) keep every consumer on the barrel through the swap.
//
// registerNavigationDriver validates the whole shape and refuses a second
// DISTINCT driver — a double registration is a wiring bug, not a warning.
// unregisterNavigationDriverForTests exists for the seam's own test files;
// the boundary guard fails if any other file references it.
// ============================================================================

import type {ElementType} from 'react';

import {reactRouterDriver} from './reactRouterDriver.js';

// The options half of the contract. `state` is deliberately `unknown`: the
// facade normalizes it (navigationState.js) before it ever reaches a driver,
// and nothing downstream may read it without proving its shape again.
export interface AppNavigateOptions {
  replace?: boolean;
  state?: unknown;
}

// Two call shapes, written as an intersection so both survive: the facade's
// path form (appNavigate) and the reserved numeric-delta form (appHistory).
// A union parameter would not do — no single signature accepts a router's
// overloaded navigate.
export type NavigateFn = ((to: string, options?: AppNavigateOptions) => unknown) & ((delta: number) => unknown);

// The subset of a router location the facade reads. `key` is deliberately
// absent (see appLocation.js), and `state` is whatever the write side stored.
export interface DriverLocation {
  pathname: string;
  search: string;
  hash: string;
  state?: unknown;
}

// The whole contract, as documented above. Anything a router supplies beyond
// these four members is invisible to the facade by construction.
export interface NavigationDriver {
  name: string;
  useNavigateFn: () => NavigateFn;
  useLocationValue: () => DriverLocation;
  LinkComponent: ElementType;
}

let registeredDriver: NavigationDriver | null = null;

function isComponent(value: unknown): boolean {
  return typeof value === 'function' || (typeof value === 'object' && value !== null);
}

// The parameter type states the contract; the runtime validation below stays
// because most callers are still JavaScript and a driver can arrive from a
// test or a future root join that the compiler never saw.
export function registerNavigationDriver(driver: NavigationDriver): NavigationDriver {
  if (!driver || typeof driver !== 'object' || Array.isArray(driver)) {
    throw new TypeError('a navigation driver must be an object');
  }
  if (typeof driver.name !== 'string' || driver.name.trim() === '') {
    throw new TypeError('a navigation driver requires a non-empty name');
  }
  if (typeof driver.useNavigateFn !== 'function' || typeof driver.useLocationValue !== 'function') {
    throw new TypeError('a navigation driver requires useNavigateFn and useLocationValue hooks');
  }
  if (!isComponent(driver.LinkComponent)) {
    throw new TypeError('a navigation driver requires a LinkComponent');
  }
  if (registeredDriver !== null && registeredDriver !== driver) {
    throw new TypeError(
      `a navigation driver is already registered (${registeredDriver.name}); refusing ${driver.name}`,
    );
  }
  registeredDriver = driver;
  return registeredDriver;
}

export function resolveNavigationDriver(): NavigationDriver {
  return registeredDriver || reactRouterDriver;
}

// Test-only escape hatch. Production code must never call this: the boundary
// guard asserts it is referenced nowhere outside this seam's own test files.
export function unregisterNavigationDriverForTests(): void {
  registeredDriver = null;
}
